import {
  CheckIcon,
  CounterClockwiseClockIcon,
  Share1Icon,
} from "@radix-ui/react-icons";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  MoreVerticalIcon,
  PencilIcon,
  ShuffleIcon,
  TrashIcon,
} from "lucide-react";
import { useState } from "react";

import { CustomNodeInstallStats } from "@/components/CustomNodeInstallStats";
import {
  FavoriteCustomNodeButton,
  FavoriteCustomNodeMenuItem,
} from "@/components/FavoriteCustomNodeButton";
import { FeedEditor } from "@/components/FeedEditor";
import { useToast } from "@/components/hooks/use-toast";
import { useEvent } from "@/components/hooks/useEvent";
import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import { Page } from "@/components/Page";
import { Spinner } from "@/components/Spinner";
import { TagList } from "@/components/TagList";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button, ButtonLink, ButtonProps } from "@/components/ui/button";
import {
  DropdownMenu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@/components/ui/menu";
import { SearchInput } from "@/components/ui/search-input";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { H1 } from "@/components/ui/typography";
import {
  CustomNodeHistoryItem,
  deleteCustomNode,
  restoreCustomNode,
  getCurrentUserQueryOptions,
  getCustomNodeQueryOptions,
  hydrateUserUsernameQueryOptions,
  myFeedsQueryOptions,
} from "@/lib/api";
import { FeedVersion } from "@/lib/types";

function AddToFeedDialog({
  nodeId,
  buttonSize,
}: {
  nodeId: number;
  buttonSize?: ButtonProps["size"];
}) {
  const [search, setSearch] = useState("");
  const { data: feeds, isFetching } = useQuery(
    myFeedsQueryOptions({ search_term: search }),
  );

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size={buttonSize}>Add to Feed</Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Add to Feed</AlertDialogTitle>
          <AlertDialogDescription>
            Choose a feed to add this custom node to.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <SearchInput
          aria-label="Search for a feed"
          placeholder="Search for a feed"
          value={search}
          onChange={setSearch}
        />
        {isFetching ? (
          <div className="flex items-center justify-center p-10">
            <Spinner />
          </div>
        ) : (
          feeds && (
            <div className="p-1 flex flex-col gap-2 border border-slate-6 dark:border-slatedark-6 rounded-lg">
              {feeds.user_algos.map((feed) => (
                <ButtonLink
                  size="sm"
                  variant="ghost"
                  className="justify-start"
                  key={feed.id}
                  to="/app/feed/$feedId/edit"
                  params={{ feedId: feed.id.toString() }}
                  search={{
                    fullscreen: true,
                    customNode: nodeId,
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Avatar
                      src={feed.avatar_url}
                      size="xs"
                      alt={feed.display_name}
                      className="rounded-md"
                    />
                    {feed.display_name}
                  </div>
                </ButtonLink>
              ))}
            </div>
          )
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction>Add</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RestoreCustomNodeButton({ nodeId }: { nodeId: number }) {
  const { toast } = useToast();
  const mutation = useMutation({
    mutationFn: () => restoreCustomNode(nodeId),
    onError: (error) => {
      console.error(error);
      toast({
        title: "Could not restore this node",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "Custom node restored",
        description: "It shows up in search again.",
      });
    },
  });

  return (
    <Button
      variant="outline"
      disabled={mutation.isPending}
      onClick={() => mutation.mutate()}
    >
      {mutation.isPending ? "Restoring..." : "Restore"}
    </Button>
  );
}

function DeleteCustomNodeDialog({
  nodeId,
  children,
}: {
  nodeId: number;
  children: React.ReactNode;
}) {
  const navigate = useNavigate({ from: "/app/custom-nodes/$nodeId/view" });
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const mutation = useMutation({
    mutationFn: () => deleteCustomNode(nodeId),
    onError: (error) => {
      console.error(error);
      toast({
        title: "Error deleting custom node",
        description: error.message,
      });
    },
    onSuccess: () => {
      toast({
        title: "Custom node deleted",
        description: "The custom node has been deleted",
      });
      return navigate({ to: "/app/custom-nodes" });
    },
  });

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild disabled={mutation.isPending}>
        {children}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Custom Node</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this custom node? This action is
            irreversible.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => mutation.mutate()}>
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export const Route = createFileRoute(
  "/app/_sidebar-layout/custom-nodes/$nodeId/view",
)({
  component: RouteComponent,
  loader: async ({ context: { queryClient }, params: { nodeId } }) => {
    const data = await queryClient.ensureQueryData(
      getCustomNodeQueryOptions(parseInt(nodeId)),
    );

    await Promise.all([
      queryClient.ensureQueryData(getCurrentUserQueryOptions()),
      queryClient.ensureQueryData(
        hydrateUserUsernameQueryOptions(data.user_id),
      ),
    ]);

    return {
      title: data.title,
    };
  },
  head: ({ loaderData }) => ({
    meta: [{ title: loaderData?.title }],
  }),
});

function CustomNodeActions({
  nodeId,
  isOwner,
  item,
  isPublic,
}: {
  nodeId: string;
  isOwner: boolean;
  item: CustomNodeHistoryItem;
  isPublic: boolean;
}) {
  const { toast } = useToast();
  const onCopyShareLink = useEvent(() => {
    navigator.clipboard
      .writeText(`https://www.graze.social/custom-nodes/${nodeId}`)
      .then(() => toast({ title: "Copied link to clipboard." }))
      .catch(console.error);
  });

  return (
    <>
      <div className="md:hidden flex gap-1">
        <AddToFeedDialog nodeId={parseInt(nodeId)} buttonSize="sm" />
        <DropdownMenu>
          <MenuTrigger asChild>
            <Button variant="outline" size="smIcon">
              <span className="sr-only">More actions</span>
              <MoreVerticalIcon />
            </Button>
          </MenuTrigger>

          <MenuContent align="end">
            {isOwner && (
              <>
                <MenuItem asChild>
                  <Link
                    to="/app/custom-nodes/$nodeId/version-history"
                    params={{ nodeId }}
                  >
                    <CounterClockwiseClockIcon />
                    Version History
                  </Link>
                </MenuItem>
                <MenuItem asChild>
                  <Link to="/app/custom-nodes/$nodeId/edit" params={{ nodeId }}>
                    <PencilIcon />
                    Edit
                  </Link>
                </MenuItem>

                <DeleteCustomNodeDialog nodeId={parseInt(nodeId)}>
                  <MenuItem onSelect={(e) => e.preventDefault()}>
                    <TrashIcon />
                    Delete
                  </MenuItem>
                </DeleteCustomNodeDialog>
              </>
            )}

            <MenuItem onSelect={onCopyShareLink}>
              <Share1Icon />
              Share
            </MenuItem>
            <MenuItem asChild>
              <Link
                to="/app/custom-nodes/create"
                search={{ remix: parseInt(nodeId) }}
              >
                <ShuffleIcon />
                Remix
              </Link>
            </MenuItem>
            <FavoriteCustomNodeMenuItem node={item} />
          </MenuContent>
        </DropdownMenu>
      </div>
      <div className="hidden md:flex items-center gap-2">
        {isOwner && (
          <>
            <DeleteCustomNodeDialog nodeId={parseInt(nodeId)}>
              <SimpleTooltip message="Delete">
                <Button variant="destructive" size="icon">
                  <span className="sr-only">Delete</span>
                  <TrashIcon />
                </Button>
              </SimpleTooltip>
            </DeleteCustomNodeDialog>
            <SimpleTooltip message="Version History">
              <ButtonLink
                to="/app/custom-nodes/$nodeId/version-history"
                params={{ nodeId }}
                variant="outline"
                size="icon"
              >
                <span className="sr-only">View version history</span>
                <CounterClockwiseClockIcon />
              </ButtonLink>
            </SimpleTooltip>
            <SimpleTooltip message="Edit">
              <ButtonLink
                to="/app/custom-nodes/$nodeId/edit"
                size="icon"
                variant="outline"
                params={{ nodeId }}
              >
                <span className="sr-only">Edit</span>
                <PencilIcon />
              </ButtonLink>
            </SimpleTooltip>
          </>
        )}
        {isPublic && (
          <SimpleTooltip message="Share">
            <Button variant="outline" size="icon" onPress={onCopyShareLink}>
              <span className="sr-only">Share</span>
              <Share1Icon />
            </Button>
          </SimpleTooltip>
        )}
        <FavoriteCustomNodeButton node={item} variant="outline" />
        <SimpleTooltip message="Remix">
          <ButtonLink
            to="/app/custom-nodes/create"
            search={{ remix: parseInt(nodeId) }}
            variant="outline"
            size="icon"
          >
            <span className="sr-only">Remix</span>
            <ShuffleIcon />
          </ButtonLink>
        </SimpleTooltip>
        <AddToFeedDialog nodeId={parseInt(nodeId)} />
      </div>
    </>
  );
}

function RouteComponent() {
  const navigate = useNavigate({ from: "/app/custom-nodes/$nodeId/view" });
  const { nodeId } = Route.useParams();
  const { data } = useSuspenseQuery(
    getCustomNodeQueryOptions(parseInt(nodeId)),
  );
  const { data: user } = useSuspenseQuery(getCurrentUserQueryOptions());
  const { data: author } = useSuspenseQuery(
    hydrateUserUsernameQueryOptions(data.user_id),
  );
  const isOwner = user.user_id === data.user_id;

  return (
    <Page>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 border-b border-slate-6 dark:border-slatedark-6 pb-4">
          <div className="flex flex-col gap-2 justify-between">
            <div className="flex justify-between items-center gap-2">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <ButtonLink
                    to="/app/custom-nodes"
                    variant="ghost"
                    size="smIcon"
                  >
                    <span className="sr-only">Back to custom nodes</span>
                    <ArrowLeftIcon />
                  </ButtonLink>
                  <div className="text-slate-dim">Custom Nodes</div>
                </div>
              </div>
              {data.public && (
                <Badge>
                  <CheckIcon />
                  Public
                </Badge>
              )}
              {/*
                Deleting a node that feeds use archives it instead of removing
                it: it keeps running everywhere it is installed but disappears
                from other people's search. Without this badge the page looks
                identical to a live node, which is how someone ends up unable
                to explain why nobody can find it.
              */}
              {data.deprecated && (
                <Badge variant="secondary" title="Hidden from search. Still running on every feed that installed it.">
                  Archived
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <H1 className="flex-1 truncate">{data.title}</H1>

              {isOwner && data.deprecated && (
                <RestoreCustomNodeButton nodeId={parseInt(nodeId)} />
              )}

              <CustomNodeActions
                nodeId={nodeId}
                isOwner={isOwner}
                item={data}
                isPublic={data.public}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2">
          {isOwner ? (
            <div className="flex gap-2 items-center text-slate-dim text-sm">
              <div className="font-semibold">Tags:</div>
              {data.tags.length === 0 && <span>None</span>}
              <TagList
                data={data}
                type="custom-node"
                size="xsIcon"
                showOnHover={false}
              />
            </div>
          ) : (
            author && (
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Avatar src={author.avatar} size="xs" alt={author.handle} />
                <div className="flex-1 truncate">@{author.handle}</div>
              </div>
            )
          )}
          <CustomNodeInstallStats {...data} />
        </div>
      </div>

      {data.description && (
        <MarkdownRenderer className="text-slate-dim text-lg">
          {data.description}
        </MarkdownRenderer>
      )}

      <FeedEditor
        feedVersion={
          {
            manifest: { filter: data.component },
            metadata: { snapshot: data.component_metadata?.snapshot },
          } as FeedVersion
        }
        rootNode={{
          title: "Custom Node",
          variant: "custom-node",
          description:
            "This is a definition of a custom node that you can share with other feeds.",
          customNodeParameters: data.component.metadata?.customNodeParameters,
          customNodeParameterGroups:
            data.component.metadata?.customNodeParameterGroups,
        }}
        onEdit={
          isOwner
            ? () => {
                navigate({
                  to: "/app/custom-nodes/$nodeId/edit",
                  params: { nodeId },
                  search: { fullscreen: true },
                }).catch(console.error);
              }
            : undefined
        }
      />
    </Page>
  );
}
